import { supabase } from '@/shared/supabase/client'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import { screenshotFixtureShiftTimeEntries } from '@/shared/screenshotSeed/synapScreenshotFixtures'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'
import type { Database } from '@/shared/supabase/database.types'

export type ShiftTimeEntryRecord = {
  id: string
  ownerUserId: string
  eventId: string | null
  locationId: string | null
  startedAt: string
  endedAt: string | null
  /** Atendimentos informados no plantão; `null` = não informado (PLT-10), nunca 0 implícito. */
  attendanceCount: number | null
  createdAt: string
  updatedAt: string
}

type ShiftTimeEntryRow = Database['public']['Tables']['shift_time_entries']['Row']

const getCurrentUserId = async (): Promise<string> => {
  const { data, error } = await supabase.auth.getUser()
  if (error) throw toRepositoryError(error, 'Não foi possível validar a sessão.')
  if (!data.user) throw toRepositoryError(new Error('Usuário não autenticado.'), 'Usuário não autenticado.', 401)
  return data.user.id
}

const toRecord = (row: ShiftTimeEntryRow): ShiftTimeEntryRecord => ({
  id: row.id,
  ownerUserId: row.owner_user_id,
  eventId: row.event_id,
  locationId: row.location_id,
  startedAt: row.started_at,
  endedAt: row.ended_at,
  attendanceCount: row.attendance_count ?? null,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
})

/** Inteiro >= 0 ou `null` (vazio). Rejeita negativo e fração antes de chegar ao banco. */
export const validateAttendanceCount = (count: number | null | undefined): void => {
  if (count == null) return
  if (!Number.isInteger(count) || count < 0) {
    throw toRepositoryError(
      new Error('Atendimentos deve ser um número inteiro maior ou igual a zero.'),
      'Atendimentos deve ser um número inteiro maior ou igual a zero.',
      422,
    )
  }
}

export const shiftTimeEntriesRepository = {
  startForEvent: async (payload: {
    eventId: string
    locationId?: string | null
  }): Promise<{ entry: ShiftTimeEntryRecord }> => {
    const userId = await getCurrentUserId()
    const open = await shiftTimeEntriesRepository.getOpenForEvent(payload.eventId)
    if (open.entry) {
      throw toRepositoryError(
        new Error('Já existe um turno em andamento para este evento.'),
        'Já existe um turno em andamento para este evento.',
        409,
      )
    }

    const { data, error } = await supabase
      .from('shift_time_entries')
      .insert({
        owner_user_id: userId,
        event_id: payload.eventId,
        location_id: payload.locationId ?? null,
      })
      .select('*')
      .single()

    if (error) throw toRepositoryError(error, 'Não foi possível iniciar o turno.')
    return { entry: toRecord(data) }
  },

  endEntry: async (
    entryId: string,
    options: { attendanceCount?: number | null } = {},
  ): Promise<{ entry: ShiftTimeEntryRecord }> => {
    validateAttendanceCount(options.attendanceCount)
    const userId = await getCurrentUserId()
    const endedAt = new Date().toISOString()
    const updatePayload: Database['public']['Tables']['shift_time_entries']['Update'] = { ended_at: endedAt }
    if (options.attendanceCount !== undefined) updatePayload.attendance_count = options.attendanceCount
    const { data, error } = await supabase
      .from('shift_time_entries')
      .update(updatePayload)
      .eq('id', entryId)
      .eq('owner_user_id', userId)
      .is('ended_at', null)
      .select('*')
      .single()

    if (error) throw toRepositoryError(error, 'Não foi possível encerrar o turno.')
    return { entry: toRecord(data) }
  },

  getOpenForEvent: async (eventId: string): Promise<{ entry: ShiftTimeEntryRecord | null }> => {
    if (isScreenshotFixtureMode()) {
      const open = screenshotFixtureShiftTimeEntries().find(
        (e) => e.eventId === eventId && e.endedAt == null,
      )
      return { entry: open ?? null }
    }
    const userId = await getCurrentUserId()
    const { data, error } = await supabase
      .from('shift_time_entries')
      .select('*')
      .eq('owner_user_id', userId)
      .eq('event_id', eventId)
      .is('ended_at', null)
      .maybeSingle()

    if (error) throw toRepositoryError(error, 'Não foi possível carregar o turno.')
    return { entry: data ? toRecord(data) : null }
  },

  listInRange: async (range: {
    start: string
    end: string
  }): Promise<{ entries: ShiftTimeEntryRecord[] }> => {
    if (isScreenshotFixtureMode()) {
      const rangeStart = new Date(range.start)
      const rangeEnd = new Date(range.end)
      const entries = screenshotFixtureShiftTimeEntries().filter((entry) => {
        const started = new Date(entry.startedAt)
        return started >= rangeStart && started < rangeEnd
      })
      return { entries }
    }
    const userId = await getCurrentUserId()
    const { data, error } = await supabase
      .from('shift_time_entries')
      .select('*')
      .eq('owner_user_id', userId)
      .gte('started_at', range.start)
      .lt('started_at', range.end)
      .order('started_at', { ascending: true })

    if (error) throw toRepositoryError(error, 'Não foi possível listar registros de turno.')
    return { entries: (data ?? []).map(toRecord) }
  },

  createManual: async (payload: {
    eventId?: string | null
    locationId?: string | null
    startedAt: string
    endedAt?: string | null
    attendanceCount?: number | null
  }): Promise<{ entry: ShiftTimeEntryRecord }> => {
    validateAttendanceCount(payload.attendanceCount)
    const userId = await getCurrentUserId()
    const { data, error } = await supabase
      .from('shift_time_entries')
      .insert({
        owner_user_id: userId,
        event_id: payload.eventId ?? null,
        location_id: payload.locationId ?? null,
        started_at: payload.startedAt,
        ended_at: payload.endedAt ?? null,
        attendance_count: payload.attendanceCount ?? null,
      })
      .select('*')
      .single()

    if (error) throw toRepositoryError(error, 'Não foi possível criar o registro de turno.')
    return { entry: toRecord(data) }
  },

  updateEntry: async (
    entryId: string,
    patch: {
      startedAt?: string
      endedAt?: string | null
      locationId?: string | null
      eventId?: string | null
      attendanceCount?: number | null
    },
  ): Promise<{ entry: ShiftTimeEntryRecord }> => {
    validateAttendanceCount(patch.attendanceCount)
    const userId = await getCurrentUserId()
    const updatePayload: Database['public']['Tables']['shift_time_entries']['Update'] = {}
    if (patch.startedAt !== undefined) updatePayload.started_at = patch.startedAt
    if (patch.endedAt !== undefined) updatePayload.ended_at = patch.endedAt
    if (patch.locationId !== undefined) updatePayload.location_id = patch.locationId
    if (patch.eventId !== undefined) updatePayload.event_id = patch.eventId
    if (patch.attendanceCount !== undefined) updatePayload.attendance_count = patch.attendanceCount

    const { data, error } = await supabase
      .from('shift_time_entries')
      .update(updatePayload)
      .eq('id', entryId)
      .eq('owner_user_id', userId)
      .select('*')
      .single()

    if (error) throw toRepositoryError(error, 'Não foi possível atualizar o registro de turno.')
    return { entry: toRecord(data) }
  },

  deleteEntry: async (entryId: string): Promise<void> => {
    const userId = await getCurrentUserId()
    const { error } = await supabase
      .from('shift_time_entries')
      .delete()
      .eq('id', entryId)
      .eq('owner_user_id', userId)

    if (error) throw toRepositoryError(error, 'Não foi possível excluir o registro de turno.')
  },
}
